import * as React from 'react'
import { FC } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'

import { TerritoryBoundary } from '@user/store/territory/typings'
import { TerritoryOverlapWarning } from './TerritoryOverlapWarning'

type Props = {
  boundary: TerritoryBoundary
  /** The territory being reshaped; it overlaps its own old outline, which is not news. */
  territoryId: number
  territoryName: string
  onCancel: () => void
  onConfirm: () => void
}

/**
 * The one place a redraw has to stop and ask (WWM-17508).
 *
 * Reshaping a territory from an assignment rule saves on the spot: the name, team, owners and
 * colour are already settled, so there is deliberately no dialog in the way. That is the right
 * default and it stays -- this appears ONLY when the new outline has strayed onto somebody
 * else's ground, which is the single case where saving silently would hide the consequence.
 *
 * "Save anyway" is a real option, in that position, because overlap is legal. The purpose is to
 * make it a decision instead of an accident.
 */
export const TerritoryOverlapConfirmDialog: FC<Props> = ({
  boundary,
  territoryId,
  territoryName,
  onCancel,
  onConfirm,
}) => (
  <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>Save this outline?</DialogTitle>

    <DialogContent>
      <DialogContentText sx={{ mb: 2 }}>
        The new outline for &ldquo;{territoryName}&rdquo; reaches into ground
        another territory already covers.
      </DialogContentText>

      <TerritoryOverlapWarning
        boundary={boundary}
        excludeTerritoryId={territoryId}
      />
    </DialogContent>

    <DialogActions>
      <Button onClick={onCancel}>Draw it again</Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        data-test-id="territory-overlap-save-anyway"
      >
        Save anyway
      </Button>
    </DialogActions>
  </Dialog>
)
